import React, { useEffect, useState } from 'react';
import { Button, Header } from 'react-frontier';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { NotFound, CartItem } from '@cocoan/components';
import { Location } from '@cocoan/components/Classes';
import { CDN_URL, PLACEHOLDER_IMG } from '../ConfigPublic';
import { addCommas } from '../UtilPublic';
import { useTitle } from '../HooksPublic';
import { useTracker } from '@cocoan/components/TrackerHook';
import API from '../APIPublic';
import moment from 'moment';

import '../style/public_order.scss';

interface Order{
	order_id: number,
	order_hash: string,
	owner_name: string,
	owner_last_name: string,
	owner_email: string,
	owner_phone: string,
	shipping_method: number,
	shipping_method_name: string,
	shipping_method_type: number,
	shipping_method_location_id: number,
	shipping_location_id: number,
	shipping_date: number,
	gift_name: string,
	gift_last_name: string,
	gift_message: string,
	gift_phone: string,
	shipping_cost: number,
	total: number,
	paid: boolean,
	shipped: boolean,
	prepared: boolean,
	date_created: number,
	shipping_location: Location,
	shipping_method_location: Location,
	items: {
		item_id: number,
		product_id: number,
		product_name: string,
		size: string,
		quantity: number,
		cost_single: number,
		cost: number
	}[],
	payments: {
		payment_id: number,
		payment_method: number,
		method_name: string,
		amount: number,
		paid: boolean,
	}[],
}

declare global {
	interface Window {
		gtag: (...args: any[]) => void;
	}
}

var PublicOrder = ()=>{
	var { hash } = useParams();
	var Pixel = useTracker();
	var navigate = useNavigate();
	var [order, setOrder] = useState<Order>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var { setTitle } = useTitle();

	useEffect(()=>{
		if(!hash || hash.length<3) return;
		API.getOrderData(hash).then(res=>{
			if(res.error) return setLoadError(res.message);
			setTitle(`Orden #CCN${res.data.order_id}`)
			setOrder(res.data);

			var is_paid = res.data.paid;
			if(!is_paid){
				if(res.data.payments && res.data.payments.length>0){
					is_paid = res.data.payments.find((a: any)=>!!a.paid);
				}
			}
			if(is_paid && !res.data.paid){
				res.data.paid = true;
			}

			if(!is_paid){
				return navigate(`/checkout/${hash}`);
			}else{
				try{
					var purchase_key = `ccn_purchase_${res.data.order_id}`;
					if(res.data && (moment().unix()-res.data.date_paid)<=(3600*2) && !localStorage.getItem(purchase_key)){
						localStorage.setItem(purchase_key, '1');
						Pixel.purchase({
							content_name: `Orden #CCN${res.data.order_id}`,
							currency: 'MXN',
							items: (res.data as Order).items.map(a=>({
								item_id: a.product_id.toString(),
								item_name: a.product_name,
								price: a.cost_single,
								quantity: a.quantity,
							})),
							shipping: (res.data as Order).shipping_cost,
							transaction_id: `CCN-${res.data.order_id}`,
							value: (res.data as Order).total,
						})
					}
				}catch(e){}
			}
		}).catch(err=>{
			return setLoadError('Hubo un error inesperado cargando la orden (LCL-1)');
		});
	}, []);
	
	if(!hash || hash.length<3) return <NotFound />
	if(loadError) return <NotFound subtext={loadError} />
	if(!order) return <Header loading text='Cargando orden...' />

	var total_products = order.items.reduce((a,b)=>a+b.cost, 0);
	var pending_payment : Order["payments"][0] = null;
	if(!order.paid && order.payments && order.payments.length>0){
		var pp = order.payments.filter(a=>!a.paid);
		if(pp.length>0) pending_payment = pp[0];
	}

	if(!order.paid && !pending_payment) return null;

	var formatDate = (unix: number)=>{
		var m = moment.unix(unix);
		var day = m.format('D');
		var months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
		var month = months[m.month()];
		var year = m.format('YYYY');
		var time = m.format('h:mm a');
		return `${day} de ${month} del ${year} a las ${time}`;
	}

	var formatDeliveryDate = (unix: number)=>{
		var m = moment.unix(unix);
		var day = m.format('D');
		var months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
		var month = months[m.month()];
		var year = m.format('YYYY');
		return `${day} de ${month} del ${year}`;
	}

	var getPickupLocation = ()=>{
		if(order.shipping_method_location){
			var loc = order.shipping_method_location;
			return `${loc.street} ${loc.exterior_number ? '#' + loc.exterior_number : ''}, ${loc.neighborhood || ''}, ${loc.zipcode || ''} ${loc.city || ''}, ${loc.state || ''}`.trim();
		}
		return 'RÍO MANZANARES #425, Del Valle, 66220 Monterrey, N.L.';
	}

	var getShippingAddress = ()=>{
		if(!order.shipping_location) return null;
		var loc = order.shipping_location;
		return `${loc.street} ${loc.exterior_number || ''}${loc.interior_number ? ' Int. ' + loc.interior_number : ''}, ${loc.neighborhood || ''}, ${loc.zipcode || ''} ${loc.city || ''}, ${loc.state || ''}`.trim();
	}

	var isPickup = order.shipping_method_type === 20;
	
	return <div className="co public-order-screen">
		<div className="confirmation-content">
			<div className="container">
				<div className="title-row">
					<h1>Pago realizado</h1>
					<span className="chocolate-emoji">🍫</span>
				</div>

				<Link className="order-number" to={`/order/${hash}`}>
					Orden N. #CCN{order.order_id}
				</Link>

				<div className="order-meta">
					Fecha de orden: {formatDate(order.date_created)}<br />
					Te hemos enviado también un correo a <strong>{order.owner_email}</strong>
				</div>

				<div className="greeting">
					¡Hola {order.owner_name}!
				</div>

				<div className="confirmation-message">
					{isPickup ? (
						<p>
							Tu compra ha sido realizada con éxito, puedes pasar a recoger tu pedido {!!order.shipping_date && <>el día <strong>{formatDeliveryDate(order.shipping_date)}</strong> </>}de 2:00 p.m a 6:00 p.m en la sucursal <strong>{getPickupLocation()}</strong>
						</p>
					) : (
						<p>
							Tu compra ha sido realizada con éxito. Tu pedido será enviado a <strong>{getShippingAddress()}</strong>
							{!!order.shipping_date && (<>
								{' '}el día <strong>{formatDeliveryDate(order.shipping_date)}</strong>
							</>)}
						</p>
					)}

					<p>
						Estaremos al pendiente de tí. Sí tienes alguna duda sobre tu pedido puedes comunicarte con nosotros al <strong>81 2944 8353</strong> y con gusto te atenderemos.
					</p>

					<p>Agradecemos tu compra y valoramos tu elección.</p>
					
					<p>Bienvenido a COCOAN.</p>
				</div>

				{!order.paid && (
					<div className="pending-payment-notice">
						<div className="notice-title">Pendiente de pago</div>
						<div>
							Esta orden está pendiente de pago. Favor de realizar el pago de <strong>{pending_payment?.method_name}</strong>
						</div>
						<Button text='Ver ficha de pago' color='black' style={{ marginTop: 15 }} iconName='file-pdf' as={Link} to={`/order/${order.order_hash}/payment`} target='_blank' />
					</div>
				)}

				<div className="invoice-link">
					<Button text='Facturar compra' color='black' as={Link} to={`/facturar?o=${order.order_hash}`} />
				</div>

				<div className="signature">
					<img src="/SINCERA_LOGO_BLACK.png" alt="Chocolatería Sincera" className="signature-img" />
				</div>
			</div>
		</div>

		<div className="confirmation-cart">
			<div className="cart-title">Resumen del pedido</div>
			<div className="items">
				{order.items.map((a, i)=>(
					<CartItem
						showSize
						key={`CRTI-${a.product_id}-${i}`}
						product={{
							product_id: a.product_id,
							product_name: a.product_name,
							price: a.cost_single,
							size: a.size,
						}}
						amount={a.quantity}
						image={`${CDN_URL}/products/${a.product_id}/0.webp`}
						placeholderImage={PLACEHOLDER_IMG}
						actions={<>
							<div style={{ marginTop: 5 }} />
							{a.quantity>1 && (
								<div className="meta">
									Precio: ${addCommas(a.cost_single)}
								</div>
							)}
							<div className="meta" style={{ marginBottom: 5 }}>
								Cantidad: {addCommas(a.quantity, true, true)}
							</div>
						</>}
					/>
				))}
			</div>

			<div className="totals">
				<div className="amount">
					<div className="label">Subtotal</div>
					<div className="value">${addCommas(total_products)}</div>
				</div>
				<div className="amount">
					<div className="label">Entrega</div>
					<div className="value">${addCommas(order.shipping_cost)}</div>
				</div>
				<div className="amount final">
					<div className="label">Total</div>
					<div className="value">${addCommas(order.total)}</div>
				</div>
			</div>
		</div>
	</div>
}

export default PublicOrder;